import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createSlots as _createSlots, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "verification-form-wrapper" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-content-center mt-4"
}
const _hoisted_3 = ["src"]
const _hoisted_4 = {
  key: 1,
  class: "grid justify-content-center my-4"
}
const _hoisted_5 = { class: "grid justify-content-center" }
const _hoisted_6 = { key: 5 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { class: "flex justify-content-center mt-2" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "flex justify-content-center verification-subfooter" }

import { Ref, computed, onMounted, ref } from "vue";
import { DateTime } from "luxon";
import axios from "axios";
import { parseDate } from "@/utils";
import {
    Alert,
    ContactAuthorizationViewModel,
    LookupTableViewModel,
    PortalFacilityDetailsViewModel, 
    PortalFacilityViewModel
} from "@/store/classes";
import { useRootStore } from "@/store";
import { VerificationFormStep as Step, VerificationType } from "@/store/enums";
import FormField from "@/components/common/FormField.vue3.vue";
import { z } from "zod";
import { RequiredDateSchema, RequiredStringSchema } from "@/components/common/schemas";
import { useFormValidation } from "@/components/common/FormValidation";
import { useRouter } from 'vue-router';
import { ExternalFacilityType } from "@/store/enums";


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactVerificationForm.vue3',
  props: {
    contactKey: {},
    verifyType: {},
    formTitle: {},
    columnClasses: {}
  },
  emits: ["load-form"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const router = useRouter();
const rootStore = useRootStore();

const currentColumnClasses = computed(() => {
    return (currentStep.value === Step.FormEntry && !!props.columnClasses)
        ? props.columnClasses
        : 'lg:col-6 xl:col-5'
})

const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);
const initializePortal = (contactKey: string, facility: PortalFacilityViewModel) => rootStore.dispatch('initializePortal', { contactKey, facility });

const currentStep = ref<Step>(Step.FormLoading);
const facilityDetails = ref<PortalFacilityDetailsViewModel>({
    lookupTables: [] as LookupTableViewModel[]
} as PortalFacilityDetailsViewModel);

const validationCode = ref<number | undefined>(undefined);
const patientIdentity = ref({
    lastName: '',
    dateOfBirth: ''
});
const patientIdentitySchema = z.object({
    lastName: RequiredStringSchema,
    dateOfBirth: RequiredDateSchema,
});
const patientIdentityValidationState = useFormValidation<z.infer<typeof patientIdentitySchema>>(
    patientIdentitySchema, 
    patientIdentity);

const facilityName = computed(() => 
    facilityDetails.value.facilityLocation?.facilityName || facilityDetails.value.facility?.facilityName);

const facilityPhoneNumber = computed(() =>
    facilityDetails.value.facilityLocation?.phoneNumber || facilityDetails.value.facility?.phoneNumber);

const facilityAddressLine = computed(() =>
    facilityDetails.value.facilityLocation?.addressLine || facilityDetails.value.facility?.addressLine);

const facilityLogoMediaUrl = computed(() =>
    facilityDetails.value.facility?.logoGuid 
        ? process.env.VUE_APP_PORTAL_BASE_URL + '/media/' + encodeURIComponent(facilityDetails.value.facility.logoGuid) 
        : ''
);

const formTitle = computed<string>(() => {
    if (!facilityName.value) 
        return ''; // default to blank until we have a facility
    const suffix = facilityDetails.value?.facility ? ' for ' + facilityName.value : '';
    return props.formTitle + suffix;
})

const dateOfBirth = computed({
    get: () => {
        const dateOfBirth = patientIdentity.value.dateOfBirth || '';
        const date = DateTime.fromISO(dateOfBirth);
        return date.isValid ? date.toFormat('MM/dd/yyyy') : dateOfBirth;
    },
    set: (value) => {
        const parsed = parseDate(value);
        patientIdentity.value.dateOfBirth = parsed.isValid ? parsed.toISODate() : value || '';
    }
});

onMounted(async () => {
    document.title = formTitle.value;
    await showLoader('contact-verification');
    try {
        if (props.verifyType === VerificationType.FacilityVisitIntake) {
            currentStep.value = Step.FacilityCodeVerification;
            return;
        }
        const response = await axios.get<PortalFacilityDetailsViewModel>(
            `/portal/verification/${props.verifyType}/${encodeURIComponent(props.contactKey)}/facility`);
        if (response.status === 204) {
            currentStep.value = Step.ResendInvite;
        } else if (response.status !== 200) {
            throw 'Invalid invite';
        } else {
            currentStep.value = Step.PatientVerification;
            facilityDetails.value = response.data;
            await initializePortal(props.contactKey, response.data.facility);
            document.title = formTitle.value;
        }
    } catch (e) {
        currentStep.value = Step.FormError;
    } finally {
        await hideLoader('contact-verification');
    }
});

async function submitVerification(): Promise<void> {
    if (!patientIdentityValidationState.validate()) return;
    try {
        await showLoader('contact-verification');
        const response = await axios.post(
            `/portal/verification/${props.verifyType}/${encodeURIComponent(props.contactKey)}/authorize`, patientIdentity.value);
        if (response.status === 204) {
            currentStep.value = Step.ResendInvite;
        } else if (response.status !== 200) {
            throw 'Invalid invite';
        } else {
            if (props.verifyType === VerificationType.Portal || props.verifyType === VerificationType.Survey) {
                // skip confirmation code
                await loadForm(); 
            } else {
                currentStep.value = Step.CodeVerification;
            }
        }
    } catch (e) {
        await showAlert({ text: 'Unable to verify identity, please try again', type: 'error' });
    } finally {
        await hideLoader('contact-verification');
    }
}

async function loadForm(): Promise<void> {
    emit('load-form', {
        currentStep,
        validationCode: validationCode.value?.toString(), 
        patientIdentity: patientIdentity.value,
        facilityDetails: facilityDetails.value 
    });
}

async function resendInvite(): Promise<void> {
    await showLoader('contact-verification');
    try {
        const baseURL = props.verifyType === VerificationType.Intake ? '/portal/intake/' : '/auth/invite/';
        await axios.post(`${baseURL}${props.contactKey}/resend`);
    } catch (e) {
        await showAlert({ text: 'Unable to resend link, please contact the system administrator', type: 'error' });
        return;
    } finally {
        await hideLoader('contact-verification');
    }
    currentStep.value = Step.ResendInviteConfirmed;
}

return (_ctx: any,_cache: any) => {
  const _component_p_input_text = _resolveComponent("p-input-text")!
  const _component_p_input_mask = _resolveComponent("p-input-mask")!
  const _component_p_button = _resolveComponent("p-button")!
  const _component_p_card = _resolveComponent("p-card")!
  const _component_p_input_number = _resolveComponent("p-input-number")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (facilityLogoMediaUrl.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("img", {
            src: facilityLogoMediaUrl.value,
            class: "max-w-full"
          }, null, 8, _hoisted_3)
        ]))
      : _createCommentVNode("", true),
    (!!formTitle.value && currentStep.value !== _unref(Step).FormError)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", {
            class: _normalizeClass(currentColumnClasses.value)
          }, [
            _createElementVNode("h2", null, _toDisplayString(formTitle.value), 1)
          ], 2)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", {
        class: _normalizeClass(currentColumnClasses.value)
      }, [
        (currentStep.value === _unref(Step).PatientVerification)
          ? (_openBlock(), _createBlock(_component_p_card, {
              key: 0,
              class: "form-card"
            }, {
              title: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode(" Verify your Identity ", -1)
              ]))]),
              content: _withCtx(() => [
                _cache[6] || (_cache[6] = _createElementVNode("p", null, "We value your privacy. Please verify your last name and date of birth to continue:", -1)),
                _createVNode(FormField, {
                  "validation-state": _unref(patientIdentityValidationState).result.value.lastName,
                  "label-key": "labels.portalIntake.lastName",
                  required: ""
                }, {
                  default: _withCtx(({ onBlur }) => [
                    _createVNode(_component_p_input_text, {
                      modelValue: patientIdentity.value.lastName,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((patientIdentity.value.lastName) = $event)),
                      maxlength: "255",
                      onBlur: onBlur
                    }, null, 8, ["modelValue", "onBlur"])
                  ]),
                  _: 1
                }, 8, ["validation-state"]),
                _createVNode(FormField, {
                  "validation-state": _unref(patientIdentityValidationState).result.value.dateOfBirth,
                  "label-key": "labels.portalIntake.dob",
                  required: ""
                }, {
                  default: _withCtx(({ onBlur }) => [
                    _createVNode(_component_p_input_mask, {
                      id: "patient-intake-dob",
                      modelValue: dateOfBirth.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateOfBirth).value = $event)),
                      mask: "99/99/9999",
                      placeholder: "mm/dd/yyyy",
                      "slot-char": "mm/dd/yyyy",
                      onBlur: onBlur
                    }, null, 8, ["modelValue", "onBlur"])
                  ]),
                  _: 1
                }, 8, ["validation-state"])
              ]),
              footer: _withCtx(() => [
                _createVNode(_component_p_button, {
                  type: "submit",
                  onClick: submitVerification
                }, {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode(" Verify ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (currentStep.value === _unref(Step).CodeVerification)
          ? (_openBlock(), _createBlock(_component_p_card, {
              key: 1,
              class: "form-card"
            }, {
              title: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                _createTextVNode(" Enter your Confirmation Code ", -1)
              ]))]),
              content: _withCtx(() => [
                _cache[9] || (_cache[9] = _createElementVNode("p", null, " We've just sent you a six digit confirmation code. Enter it below to continue: ", -1)),
                _createVNode(FormField, {
                  "label-key": "labels.portalIntake.confirmationCode",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_p_input_number, {
                      modelValue: validationCode.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((validationCode).value = $event)),
                      format: false,
                      min: 0,
                      max: 999999
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _cache[10] || (_cache[10] = _createElementVNode("small", null, "If you don't receive the code within five minutes, refresh and try again.", -1))
              ]),
              footer: _withCtx(() => [
                _createVNode(_component_p_button, {
                  type: "submit",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (loadForm()))
                }, {
                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                    _createTextVNode(" Confirm ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (currentStep.value === _unref(Step).ResendInviteConfirmed)
          ? (_openBlock(), _createBlock(_component_p_card, { key: 2 }, {
              title: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                _createTextVNode(" New Link Sent ", -1)
              ]))]),
              content: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                _createElementVNode("p", null, "A new link has been sent. Please check your email to continue.", -1)
              ]))]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (currentStep.value === _unref(Step).ResendInvite)
          ? (_openBlock(), _createBlock(_component_p_card, {
              key: 3,
              class: "form-card mt-4"
            }, {
              title: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                _createTextVNode(" Link Expired ", -1)
              ]))]),
              content: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                _createElementVNode("p", null, "This link has expired. Click below to resend a new link to the email address on file:", -1)
              ]))]),
              footer: _withCtx(() => [
                _createVNode(_component_p_button, {
                  type: "submit",
                  onClick: resendInvite
                }, {
                  default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                    _createTextVNode(" Resend Link ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (currentStep.value === _unref(Step).FormError)
          ? (_openBlock(), _createBlock(_component_p_card, {
              key: 4,
              class: "mt-4 form-card"
            }, _createSlots({
              title: _withCtx(() => [
                _cache[17] || (_cache[17] = _createTextVNode(" Page Not Available ", -1))
              ]),
              _: 2
            }, [
              (_ctx.verifyType === _unref(VerificationType).Intake || _ctx.verifyType === _unref(VerificationType).Invite)
                ? {
                    name: "content",
                    fn: _withCtx(() => [
                      _cache[18] || (_cache[18] = _createElementVNode("p", null, " This link is no longer available. If you need assistance, contact your provider. Click below to log in to the portal: ", -1))
                    ]),
                    key: "0"
                  }
                : undefined,
              (_ctx.verifyType === _unref(VerificationType).Intake || _ctx.verifyType === _unref(VerificationType).Invite)
                ? {
                    name: "footer",
                    fn: _withCtx(() => [
                      _createVNode(_component_p_button, {
                        label: "Log In",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(router).push({ path: '/login', query: { facilityType: _unref(ExternalFacilityType).NhMessaging } })))
                      })
                    ]),
                    key: "1"
                  }
                : undefined
            ]), 1024))
          : _createCommentVNode("", true),
        (currentStep.value === _unref(Step).FormEntry)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _renderSlot(_ctx.$slots, "default")
            ]))
          : _createCommentVNode("", true)
      ], 2)
    ]),
    (!!facilityName.value && currentStep.value !== _unref(Step).FormError)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", {
              class: _normalizeClass([currentColumnClasses.value, "text-center"])
            }, [
              _createTextVNode(_toDisplayString(facilityName.value) + " ", 1),
              (facilityPhoneNumber.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_9, " · " + _toDisplayString(facilityPhoneNumber.value), 1))
                : _createCommentVNode("", true),
              _cache[19] || (_cache[19] = _createTextVNode()),
              (facilityAddressLine.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_10, " · " + _toDisplayString(facilityAddressLine.value), 1))
                : _createCommentVNode("", true)
            ], 2)
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", {
              class: _normalizeClass([currentColumnClasses.value, "text-center"])
            }, [...(_cache[20] || (_cache[20] = [
              _createTextVNode(" Powered by ", -1),
              _createElementVNode("a", { href: "https://www.nethealth.com/" }, "Net Health", -1)
            ]))], 2)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})